import React, { useEffect, useState, useCallback } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { Sprout, Users, TrendingUp, ChevronDown, Award, Briefcase, BarChart } from 'lucide-react';
import Training2 from './Training2';

const VALUE_PILLARS = [
  { icon: <Award className="w-10 h-10 text-green-500" />, title: '1,000+ Students', text: 'Trained and certified across Maharashtra.' },
  { icon: <Briefcase className="w-10 h-10 text-blue-500" />, title: 'Buyback Network', text: 'Secure commercial channels for your harvest.' },
  { icon: <BarChart className="w-10 h-10 text-purple-500" />, title: 'Complete Setup', text: 'From low-cost design to government subsidies.' },
];

const TRAINING_FEATURES = [
  { icon: <Sprout className="w-6 h-6 text-green-500" />, text: 'Low-cost setup management & company standards' },
  { icon: <Users className="w-6 h-6 text-blue-500" />, text: 'Contract farming collective network access' },
  { icon: <TrendingUp className="w-6 h-6 text-purple-500" />, text: 'Digital marketing & brand strategy' },
  { icon: <Award className="w-6 h-6 text-indigo-500" />, text: 'Government scheme mapping & DIC subsidies' },
];

const Training: React.FC = () => {
  const [showTraining2, setShowTraining2] = useState(false);
  const controls = useAnimation();

  const handleScroll = useCallback(() => {
    const scrollPosition = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    
    if (scrollPosition > (documentHeight - windowHeight) * 0.3) {
      setShowTraining2(true);
      controls.start({ opacity: 1, y: 0 });
    }
  }, [controls]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 font-sans antialiased selection:bg-green-500 selection:text-white">
      {/* Hero Section */}
      <div className="relative bg-cover bg-center py-28 md:py-36 flex-grow flex items-center justify-center" style={{backgroundImage: 'url(/images/Training.webp)'}}>
        <div className="absolute inset-0 bg-black/70"></div>
        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center text-white">
          <motion.h1 
            className="text-4xl sm:text-5xl md:text-6xl font-extrabold mb-6 tracking-tight leading-tight"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Professional Grade <br/>
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-blue-400">
              Mushroom Cultivation Training
            </span>
          </motion.h1>
          <motion.p 
            className="text-lg md:text-xl mb-10 max-w-2xl mx-auto text-gray-300 font-medium"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Empowering 1,000+ farmers and entrepreneurs across Maharashtra with high-yield tech, buyback security, and expert setup management.
          </motion.p>
          <motion.a 
            href="#training-offerings"
            className="bg-gradient-to-r from-green-500 to-blue-600 text-white px-8 py-3.5 rounded-full text-base font-bold shadow-xl inline-block hover:brightness-110 transition duration-200"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            Explore Programs
          </motion.a>
        </div>
        <motion.div 
          className="absolute bottom-6 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        >
          <ChevronDown size={32} color="white" className="opacity-60" />
        </motion.div>
      </div>

      {/* Quick Stats Grid */}
      <section className="py-12 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 md:grid-cols-3 gap-6">
          {VALUE_PILLARS.map((pillar, idx) => (
            <motion.div 
              key={idx} 
              className="p-6 bg-gray-800/40 rounded-2xl border border-gray-800 flex items-center space-x-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
            >
              <div className="p-2 bg-gray-800 rounded-xl">{pillar.icon}</div>
              <div>
                <h4 className="text-base font-bold text-gray-100">{pillar.title}</h4>
                <p className="text-xs text-gray-400 mt-0.5">{pillar.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Core Ecosystem Objectives */}
      <section id="training-objective" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">Grow Together, Scale Globally</h2>
            <p className="text-gray-500 mt-2 text-sm md:text-base">We combine simple operations with industry-standard commercial support channels.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {TRAINING_FEATURES.map((item, index) => (
              <motion.div
                key={index}
                className="bg-gray-50 p-5 rounded-xl border border-gray-100 flex items-center space-x-4 hover:border-gray-200 transition-colors"
                whileHover={{ scale: 1.01 }}
              >
                <div className="bg-white p-2.5 rounded-lg border border-gray-100 shadow-sm flex-shrink-0">
                  {item.icon}
                </div>
                <span className="text-gray-700 font-semibold text-sm md:text-base">{item.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lazy Loaded Animated Offerings */}
      <div id="training-offerings">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={controls}
          transition={{ duration: 0.5 }}
        >
          {showTraining2 && <Training2 />}
        </motion.div>
      </div>
    </div>
  );
};

export default Training;